import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  createContext,
  useContext,
  ReactNode,
} from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextType {
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  success: (title: string, message?: string, duration?: number) => void;
  error: (title: string, message?: string, duration?: number) => void;
  warning: (title: string, message?: string, duration?: number) => void;
  info: (title: string, message?: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// For leaf primitives (e.g. IdCell) where toast feedback is a nice-
// to-have rather than a core feature: returns `null` instead of
// throwing when no provider is mounted, so the component still works
// inside isolated tests or contexts that haven't wired up the
// provider tree.
//
// USAGE BOUNDARY: only for leaf UI primitives in `components/ui/`
// where toast is auxiliary signal. Business code (mutations, route
// handlers, user-driven actions) MUST use `useToast` so a missing
// `ToastProvider` is loud rather than silent. If you find yourself
// reaching for this in a feature-level component, you probably want
// `useToast` and a regression test that asserts the provider is
// mounted.
export const useOptionalToast = (): ToastContextType | null => {
  return useContext(ToastContext);
};

const ToastIcon = ({ type }: { type: ToastType }) => {
  const iconMap = {
    success: CheckCircle,
    error: XCircle,
    warning: AlertCircle,
    info: Info,
  };

  const colorMap = {
    success: 'text-success',
    error: 'text-destructive',
    warning: 'text-warning',
    info: 'text-accent',
  };

  const Icon = iconMap[type];
  return <Icon className={`w-5 h-5 ${colorMap[type]}`} />;
};

const ToastItem = ({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Enter animation
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (toast.duration !== 0) {
      const timer = setTimeout(() => {
        handleRemove();
      }, toast.duration || 5000);

      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  useEffect(() => {
    if (!isLeaving) return;
    const timer = setTimeout(() => onRemove(toast.id), 300);
    return () => clearTimeout(timer);
  }, [isLeaving, onRemove, toast.id]);

  const handleRemove = () => {
    setIsLeaving(true);
  };

  const colorMap = {
    success: 'bg-success/20 border-success/30',
    error: 'bg-destructive/10 border-destructive/20',
    warning: 'bg-warning/20 border-warning/30',
    info: 'bg-accent/10 border-accent/20',
  };

  const textColorMap = {
    success: 'text-success',
    error: 'text-destructive',
    warning: 'text-warning',
    info: 'text-accent',
  };

  // Errors interrupt; everything else is a passive status update.
  // (ARIA 1.2: role=alert is implicitly aria-live=assertive,
  // role=status is aria-live=polite.) Beyond a11y this is also the
  // selector e2e specs use to assert toast visibility.
  const role = toast.type === 'error' ? 'alert' : 'status';

  return (
    <div
      role={role}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      className={`
        relative mb-2 p-4 rounded-lg border shadow-lg max-w-sm w-full
        transform transition-all duration-300 ease-in-out
        ${colorMap[toast.type]}
        ${isVisible && !isLeaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div className="flex items-start gap-3">
        <ToastIcon type={toast.type} />

        <div className="flex-1 min-w-0">
          <h4 className={`text-sm font-medium ${textColorMap[toast.type]}`}>{toast.title}</h4>
          {toast.message && (
            <p className={`text-sm mt-1 ${textColorMap[toast.type]} opacity-90`}>{toast.message}</p>
          )}

          {toast.action && (
            <button
              onClick={toast.action.onClick}
              className={`
                text-xs font-medium underline mt-2 hover:no-underline
                ${textColorMap[toast.type]}
              `}
            >
              {toast.action.label}
            </button>
          )}
        </div>

        <button
          onClick={handleRemove}
          className={`
            flex-shrink-0 p-1 rounded-md hover:bg-surface-3 transition-colors
            ${textColorMap[toast.type]}
          `}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const ToastContainer = () => {
  const context = useContext(ToastContext);
  if (!context) return null;

  const { toasts } = context as ToastContextType & { toasts: Toast[] };

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col items-end">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={context.removeToast} />
      ))}
    </div>
  );
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // The six callables below are mount-stable on purpose: nothing they do
  // depends on the current list, so a consumer holding `success` in a
  // dependency array must not be invalidated by someone else's notification.
  // `setToasts` is React state and permanently stable, and both writers use the
  // functional form, so neither reads a captured `toasts`.
  const addToast = useCallback((toastData: Omit<Toast, 'id'>) => {
    const id = crypto.randomUUID();
    const toast: Toast = {
      id,
      duration: 5000,
      ...toastData,
    };

    setToasts((prev) => [...prev, toast]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const success = useCallback(
    (title: string, message?: string, duration?: number) => {
      addToast({ type: 'success', title, message, duration });
    },
    [addToast],
  );

  const error = useCallback(
    (title: string, message?: string, duration?: number) => {
      addToast({ type: 'error', title, message, duration });
    },
    [addToast],
  );

  const warning = useCallback(
    (title: string, message?: string, duration?: number) => {
      addToast({ type: 'warning', title, message, duration });
    },
    [addToast],
  );

  const info = useCallback(
    (title: string, message?: string, duration?: number) => {
      addToast({ type: 'info', title, message, duration });
    },
    [addToast],
  );

  // The VALUE still changes when `toasts` does — that is how `ToastContainer`
  // learns to paint. Only the members are frozen.
  const contextValue = useMemo<ToastContextType & { toasts: Toast[] }>(
    () => ({
      toasts,
      addToast,
      removeToast,
      success,
      error,
      warning,
      info,
    }),
    [toasts, addToast, removeToast, success, error, warning, info],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};
